import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle, DialogHeader, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { UserPlus, Trash2, KeyRound, Copy, Package } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { v: "citizen", l: "Cittadino" },
  { v: "business", l: "Azienda" },
  { v: "operator", l: "Operatore Comune" },
];

const roleLabel = (r) => ({ admin: "Admin", operator: "Operatore", citizen: "Cittadino", business: "Azienda" }[r] || r);
const roleBadge = (r) => ({
  admin: "bg-purple-100 text-purple-700",
  operator: "bg-blue-100 text-blue-700",
  citizen: "bg-emerald-100 text-emerald-700",
  business: "bg-amber-100 text-amber-700",
}[r] || "bg-gray-100 text-gray-700");

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "citizen", tax_code: "", company_name: "" });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/admin/users"); setUsers(data); }
    catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      const payload = { email: form.email.trim(), name: form.name.trim(), role: form.role };
      if (form.role === "business") { payload.company_name = form.company_name || form.name; payload.tax_code = form.tax_code; }
      else if (form.role === "citizen") { payload.tax_code = form.tax_code; }
      const { data } = await api.post("/admin/users", payload);
      setCreated({ email: data.email, temp_password: data.temp_password });
      toast.success("Utente creato");
      setForm({ email: "", name: "", role: "citizen", tax_code: "", company_name: "" });
      setOpen(false);
      await load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  const del = async (u) => {
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Utente eliminato"); await load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const reset = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      setCreated({ email: u.email, temp_password: data.temp_password });
    } catch (e) { toast.error(formatError(e)); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copiato"); };

  return (
    <Layout
      title="Gestione utenze"
      subtitle="Crea, elimina e reimposta le utenze di cittadini, aziende e operatori."
      actions={
        <>
          <Link to="/admin/reams"><Button variant="outline" className="rounded-full gap-2" data-testid="btn-reams"><Package className="w-4 h-4" /> Risme barcode</Button></Link>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="rounded-full gap-2" data-testid="btn-new-user"><UserPlus className="w-4 h-4" /> Nuovo utente</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Crea utenza</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Ruolo</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v} data-testid={`role-opt-${r.v}`}>{r.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" /></div>
                <div><Label>{form.role === "business" ? "Referente" : "Nome completo"}</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" /></div>
                {form.role === "business" && <div><Label>Ragione sociale</Label><Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="new-user-company" /></div>}
                {(form.role === "citizen" || form.role === "business") && <div><Label>{form.role === "business" ? "Partita IVA / CF" : "Codice fiscale"}</Label><Input value={form.tax_code} onChange={(e) => setForm({ ...form, tax_code: e.target.value })} data-testid="new-user-tax" /></div>}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>Annulla</Button>
                <Button onClick={create} disabled={busy || !form.email || !form.name} data-testid="new-user-submit">Crea utente</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      }
    >
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>Email</TableHead><TableHead>Ruolo</TableHead><TableHead>Creato</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} className="hover:bg-gray-50" data-testid={`user-row-${u.id}`}>
                  <TableCell className="font-medium">{u.name}{u.company_name && <span className="block text-xs text-muted-foreground">{u.company_name}</span>}</TableCell>
                  <TableCell className="text-sm">{u.email}</TableCell>
                  <TableCell><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${roleBadge(u.role)}`}>{roleLabel(u.role)}</span></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString("it-IT") : "—"}</TableCell>
                  <TableCell className="text-right pr-6 space-x-1">
                    <Button variant="ghost" size="sm" onClick={() => reset(u)} data-testid={`reset-${u.id}`}><KeyRound className="w-4 h-4" /></Button>
                    {u.role !== "admin" && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button variant="ghost" size="sm" data-testid={`del-${u.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Eliminare {u.name}?</AlertDialogTitle><AlertDialogDescription>L'azione è irreversibile. I conferimenti passati verranno mantenuti a fini storici.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Annulla</AlertDialogCancel><AlertDialogAction onClick={() => del(u)} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Temp password display */}
      <Dialog open={!!created} onOpenChange={(v) => !v && setCreated(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Password temporanea</DialogTitle></DialogHeader>
          <div className="text-sm space-y-3">
            <div>Comunica queste credenziali all'utente. Al primo accesso dovrà cambiare la password.</div>
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg font-mono text-sm space-y-2">
              <div className="flex items-center justify-between"><span>{created?.email}</span><Button variant="ghost" size="sm" onClick={() => copy(created.email)}><Copy className="w-3.5 h-3.5" /></Button></div>
              <div className="flex items-center justify-between"><span className="font-bold" data-testid="temp-password">{created?.temp_password}</span><Button variant="ghost" size="sm" onClick={() => copy(created.temp_password)}><Copy className="w-3.5 h-3.5" /></Button></div>
            </div>
          </div>
          <DialogFooter><Button onClick={() => setCreated(null)}>Chiudi</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
